import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useMilestone } from "../../contexts/MilestoneContext";
import { extractIdFromSlug } from "../../utils/slug";
import SEO from "../SEO";
import PropertyAmenities from "./PropertyAmenities";
import PropertyDetail from "./PropertyDetail";
import PropertyRoomDetail from "./PropertyRoomDetail";
import PropertyRules from "./PropertyRules";

import { Property } from "../../models/property.model";
import { propertyService } from "../../service/property.service";
import Footer from "../Footer";
import LocationMap from "./LocationMap";
import PropertyTerms from "./PropertyTerms";

const ViewProperty: React.FC = () => {
  const location = useLocation();
  const { propertySlug } = useParams();
  const derivedId =
    extractIdFromSlug(propertySlug || "") || location.state?.propertyId;
  const propertyId = derivedId;
  const checkIn = location.state?.checkIn;
  const checkOut = location.state?.checkOut;
  // Get roomTypeIds array from navigation state (if coming from filter with multiple room types selected)
  // or fallback to single roomTypeId if present (for backward compatibility)
  const roomTypeIds: number[] = location.state?.roomTypeIds 
    ? (Array.isArray(location.state.roomTypeIds) ? location.state.roomTypeIds : [location.state.roomTypeIds])
    : (location.state?.roomTypeId ? [Number(location.state.roomTypeId)] : []);
  const noOfAdults = location.state?.noOfAdults;
  const noOfChildrens = location.state?.noOfChildrens;
  const [property, setProperty] = useState<Property | null>(null);
  const { setCurrentStep } = useMilestone();

  // Set step to 1 when viewing property details
  useEffect(() => {
    setCurrentStep(1);
  }, [setCurrentStep]);

  // Debug logging
  useEffect(() => {
    console.log("ViewProperty - Received in location.state - DETAILED:", {
      noOfAdults,
      noOfChildrens,
      checkIn,
      checkOut,
      checkInType: typeof checkIn,
      checkOutType: typeof checkOut,
      checkInLength: checkIn?.length,
      checkOutLength: checkOut?.length,
      fullState: location.state,
      derivedId,
      roomTypeIds,
    });
  }, [
    location.state,
    noOfAdults,
    noOfChildrens,
    checkIn,
    checkOut,
    derivedId,
    roomTypeIds,
  ]);

  interface PropertyProps {
    property: Property | null;
  }

  useEffect(() => {
    if (propertyId) {
      const PropertyDetail = propertyService
        .getById(propertyId, true)
        .then(setProperty)
        .catch(console.error);
    }
  }, [propertyId]);
  console.log(
    "ViewProperty component mounted or propertyId changed:",
    PropertyDetail
  );
  return (
    <>
      <SEO
        title={property ? property.name : "Property Details"}
        description={
          property?.description ||
          "View corporate housing property details, amenities, rooms and location."
        }
        canonical={
          property
            ? `${window.location.origin}/property/${property.name
                ?.toLowerCase()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")}-${property.id}`
            : undefined
        }
        jsonLd={
          property
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "Hotel",
                  name: property.name,
                  description: property.description,
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: property.address,
                    addressLocality: property.city?.name,
                    postalCode: property.zipCode,
                  },
                  url: `${window.location.origin}/property/${property.name
                    ?.toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, "")
                    .replace(/\s+/g, "-")}-${property.id}`,
                },
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: window.location.origin + "/",
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: "Properties",
                      item: window.location.origin + "/b2b-property",
                    },
                    {
                      "@type": "ListItem",
                      position: 3,
                      name: property.name,
                      item: `${window.location.origin}/property/${property.name
                        ?.toLowerCase()
                        .replace(/[^a-z0-9\s-]/g, "")
                        .replace(/\s+/g, "-")}-${property.id}`,
                    },
                  ],
                },
              ]
            : undefined
        }
      />
      {/* <BookingMilestone /> */}
      <div className="content has-page-header view-property">
        {/* <div className="content-header  shadow-sm">
          <div className='container d-flex flex-row align-items-center justify-content-between gap-1 '>
            <h6 className="_600  page-heading  " >
              {property ? property.name : 'Property Details'}
            </h5>
          </div>
        </div> */}

        <div className="content-body">
          <div className="container mt-4">
            <div className="row">
              <div className="col-md-12 mb-3">
                <PropertyDetail property={property} />
              </div>
            </div>
            <div className="card border-0 shadow-sm">
              <div className="row">
                <div className="col-md-10 mb-3">
                  <div className=" p-4">
                    <h5 className="fw-bold">Amenities</h5>
                    <div className="row align-items-center">
                      <div className="col p-0">
                        {propertyId && (
                          <PropertyAmenities
                            propertyId={propertyId}
                            viewMode="minimal"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-2 mb-3">
                  <div className="col-auto">
                    <div className="vp-map-card border rounded p-2 bg-white mt-10">
                      <div className="vp-map-thumb position-relative">
                        <img
                          src={"/Images/map.jpg"}
                          alt="map"
                          className="position-absolute top-0 start-0 w-100 h-100"
                        />
                        <a
                          href="#location-map"
                          onClick={(e) => {
                            e.preventDefault();
                            document
                              .getElementById("location-map")
                              ?.scrollIntoView({ behavior: "smooth" });
                          }}
                          className="vp-map-link position-absolute start-50 top-50 translate-middle"
                        >
                          View in map
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-12 mb-3">
                <PropertyRoomDetail
                  propertyId={propertyId}
                  checkIn={checkIn}
                  checkOut={checkOut}
                  roomTypeIds={roomTypeIds}
                  noOfAdults={noOfAdults}
                  noOfChildrens={noOfChildrens}
                />
                {/* Debug the props being passed */}
                {(() => {
                  console.log("Props being passed to PropertyRoomDetail:", {
                    propertyId,
                    checkIn,
                    checkOut,
                    roomTypeIds,
                    noOfAdults,
                    noOfChildrens,
                  });
                  return null;
                })()}
              </div>
              {/* <div className="col-md-12 mb-3">
                <div className="shadow-sm rounded-4 bg-white p-4">
                  <h5 className="fw-bold mb-3">Amenities</h5>
                  {propertyId && <PropertyAmenities propertyId={propertyId} viewMode="minimal" />}
                </div>
              </div> */}
              <div className="col-md-12 mb-3">
                <div className="shadow-sm rounded-4 bg-white p-4 vp-card">
                  <h5 className="fw-bold mb-1">Terms and Conditions</h5>
                  <PropertyRules property={property} />
                  {/* RMC-specific terms for B2B users */}
                  {propertyId && <PropertyTerms propertyId={propertyId} />}
                </div>
              </div>

              {/* Terms and Location */}
              <div className="col-md-12">
                <div className="row g-4 h-100">
                  {/* Terms */}
                  {/* <div className="col-12">
                    <div
                      className="card border-0 shadow-sm"
                      style={{ borderRadius: "12px" }}
                    >
                      <div className="card-body p-4">
                        <h5 className="card-title fw-semibold mb-3">
                          Terms and Conditions
                        </h5>
                        <PropertyRules property={property} />
                      </div>
                    </div>
                  </div> */}

                  {/* Location */}
                  <div className="col-12">
                    <div className="card border-0 shadow-sm vp-card">
                      <div className="card-body p-4">
                        <h5 className="card-title fw-semibold mb-3">
                          Location
                        </h5>
                        <LocationMap property={property} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* <PropertyAmenities propertyId={propertyId} /> */}
              <Footer />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewProperty;
